/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import {
  Allergies,
  ChoicePage,
  CompanionsPage,
  CreateNewPasswordPage,
  Dashboard,
  DonePage,
  EventSwitcherPage,
  FlightForm,
  ForgotPasswordPage,
  GuestDetailsPage,
  GuestPreferencesPage,
  LoginPage,
  Notes,
  Notifications,
  PersonalInfoForm,
  Preferences,
  RSVP,
  UnsubscribePage,
} from "pages";
import { ReservationForm } from "pages/Forms";
import { ExitPage } from "pages/On-boarding";
import { RegistrationLinkPage } from "pages/RegisterLink";
import GeneralAllergies from "pages/choices/pages/GeneralAllergies/GeneralAllergies";
import MedicalConditions from "pages/choices/pages/MedicalConditions/MedicalConditions";
import { useEffect } from "react";
import { Outlet, useNavigate, useRoutes } from "react-router-dom";
import ROUTES from "routes";
import {
  DynamicRouteManager,
  FallbackManager,
  RouteManager,
} from "./RouterManagers";
import PageNoAccess from "pages/FallbackPages/PageNoAccess";
import WelcomePage from "pages/WelcomPage";
import { WebPages } from "pages/WebPages";
import { SafeComponent } from "./RouterManagers/RouteManager";
import { Header } from "components";

const relative = (path: string) =>
  path.startsWith("/") ? path.slice(1) : path;

const RootLayout = () => {
  return (
    <>
      <Header />
      <Outlet />
    </>
  );
};

const RoutesWrapper = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleCanceledEvent = () => {
      const isCanceledItem = localStorage.getItem("canceledEvent");
      if (isCanceledItem) {
        navigate(ROUTES.noAccess, { replace: true });
        localStorage.removeItem("canceledEvent");
      }
    };
    window.addEventListener("canceledEventSet", handleCanceledEvent);
    return () => {
      window.removeEventListener("canceledEventSet", handleCanceledEvent);
    };
  }, [navigate]);

  const routes = useRoutes([
    {
      element: <RootLayout />,
      children: [
        {
          path: ROUTES.login,
          element: (
            <SafeComponent>
              <LoginPage />
            </SafeComponent>
          ),
        },
        {
          path: ROUTES.forgotPassword,
          element: (
            <SafeComponent>
              <ForgotPasswordPage />
            </SafeComponent>
          ),
        },
        // Step-token page (contract M1): entered after /login or /magic-link
        // returns is_password_reset_required: true. Authorized by the step
        // token in sessionStorage, not the session — so no RouteManager, and
        // unscoped because the step response carries no events.
        {
          path: ROUTES.createNewPassword,
          element: (
            <SafeComponent>
              <CreateNewPasswordPage />
            </SafeComponent>
          ),
        },
        {
          path: ROUTES.eventSwitcher,
          element: (
            <SafeComponent>
              <EventSwitcherPage />
            </SafeComponent>
          ),
        },
        {
          path: ROUTES.noAccess,
          element: <PageNoAccess />,
        },
        {
          path: ROUTES.registerLink.base + "/*",
          element: (
            <SafeComponent>
              <RegistrationLinkPage />
            </SafeComponent>
          ),
        },

        // Parent route with :eventId - all children use relative paths from ROUTES
        {
          path: "/:eventId",
          element: <Outlet />,
          children: [
            {
              path: relative(ROUTES.login),
              element: (
                <SafeComponent>
                  <LoginPage />
                </SafeComponent>
              ),
            },
            {
              path: relative(ROUTES.registerLink.base) + "/*",
              element: (
                <SafeComponent>
                  <RegistrationLinkPage />
                </SafeComponent>
              ),
            },
            {
              path: relative(ROUTES.forgotPassword),
              element: (
                <SafeComponent>
                  <ForgotPasswordPage />
                </SafeComponent>
              ),
            },
            {
              path: relative(ROUTES.createNewPassword),
              element: (
                <SafeComponent>
                  <CreateNewPasswordPage />
                </SafeComponent>
              ),
            },
            {
              path: relative(ROUTES.welcome),
              element: <WelcomePage />,
            },
            {
              path: relative(ROUTES.onboard.invitation),
              element: (
                <RouteManager>
                  <RSVP />
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.onboard.registration),
              element: (
                <RouteManager>
                  <CompanionsPage />
                </RouteManager>
              ),
            },

            {
              path: relative(ROUTES.onboard.done),
              element: <DonePage />,
            },
            {
              path: relative(ROUTES.choices.base(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <ChoicePage />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.onboard.personalInfo(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <PersonalInfoForm />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.choices.preferences(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <Preferences />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.choices.allergies(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <Allergies />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.choices.generalAllergies(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <GeneralAllergies />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.choices.medicalConditions(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <MedicalConditions />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.choices.notes(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <Notes />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.dashboard.base),
              element: (
                <RouteManager>
                  <Dashboard />
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.dashboard.notifications),
              element: (
                <RouteManager>
                  <Notifications />
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.webpages.base(":pageSlug")),
              element: <WebPages />,
            },
            {
              path: relative(ROUTES.dashboard.guest(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <GuestDetailsPage />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.dashboard.reviewInfo(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <PersonalInfoForm isEditing={true} />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.dashboard.guestPreferences(":userId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <GuestPreferencesPage />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.dashboard.flight(":userId", ":flightId")),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <FlightForm />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(
                ROUTES.dashboard.generalFlight(":userId", ":flightId"),
              ),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <FlightForm />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(
                ROUTES.dashboard.reservation(":userId", ":reservationId"),
              ),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <ReservationForm />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(
                ROUTES.dashboard.generalReservation(
                  ":userId",
                  ":reservationId",
                ),
              ),
              element: (
                <RouteManager>
                  <DynamicRouteManager>
                    <ReservationForm />
                  </DynamicRouteManager>
                </RouteManager>
              ),
            },
            {
              path: relative(ROUTES.unsubscribe),
              element: <UnsubscribePage />,
            },
            {
              path: relative(ROUTES.onboard.exit),
              element: <ExitPage />,
            },
          ],
        },
        {
          path: "*",
          element: <FallbackManager />,
        },
      ],
    },
  ]);
  return routes;
};

export default RoutesWrapper;
